import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Customer } from '../../types/customer';
import toast from 'react-hot-toast';
import {
  PaperAirplaneIcon,
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface SendCustomerLoginInviteProps {
  customer: Customer;
  onSuccess?: () => void;
}

const SendCustomerLoginInvite: React.FC<SendCustomerLoginInviteProps> = ({ customer, onSuccess }) => {
  const [isSending, setIsSending] = useState(false);

  const generateToken = () => {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleSendInvite = async () => {
    if (customer.hasUserAccount) {
      toast.error('Ez az ügyfél már rendelkezik felhasználói fiókkal');
      return;
    }

    setIsSending(true);

    try {
      // Generate unique token
      const token = generateToken();
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + 7); // 7 days expiry

      // Update customer with registration token
      await updateDoc(doc(db, 'customers', customer.id), {
        registrationToken: token,
        registrationTokenExpiry: expiryDate,
        updatedAt: serverTimestamp()
      });

      // Generate signup link
      const signupLink = `${window.location.origin}/customer-signup/${token}`;

      // Send email (TODO: implement email service)
      // For now, just copy to clipboard
      await navigator.clipboard.writeText(signupLink);

      toast.success('Regisztrációs link vágólapra másolva! (Email küldés hamarosan elérhető)');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error sending invite:', error);
      toast.error('Hiba történt a meghívó küldése során');
    } finally {
      setIsSending(false);
    }
  };

  // Check if customer already has account
  if (customer.hasUserAccount) {
    return (
      <button
        disabled
        className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-green-700 bg-green-100 rounded-md dark:bg-green-900/30 dark:text-green-300 cursor-not-allowed"
        title="Már rendelkezik felhasználói fiókkal"
      >
        <CheckCircleIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
        <span className="hidden sm:inline ml-1">Van fiókja</span>
      </button>
    );
  }

  // Check if invite is pending
  if (customer.registrationToken && customer.registrationTokenExpiry) {
    const isExpired = new Date(customer.registrationTokenExpiry) < new Date();
    
    if (!isExpired) {
      return (
        <button
          onClick={handleSendInvite}
          disabled={isSending}
          className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-yellow-700 bg-yellow-100 rounded-md hover:bg-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:hover:bg-yellow-900/50 transition-colors"
          title="Meghívó elküldve, kattints az újraküldéshez"
        >
          <ClockIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
          <span className="hidden sm:inline ml-1">Függőben</span>
        </button>
      );
    }
  }

  return (
    <button
      disabled={true}
      className="inline-flex items-center px-1 sm:px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-gray-400 bg-gray-100 rounded-md dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed opacity-50"
      title="Meghívó funkció jelenleg nem elérhető"
    >
      <PaperAirplaneIcon className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-1" />
      <span className="hidden sm:inline ml-1">Meghívó</span>
    </button>
  );
};

export default SendCustomerLoginInvite;
